/**
 * The lot page's overview facts and description, and the side panel's
 * valuation, energy and decoded-rooms panels, with the evidence the workbench
 * dossier showed: tenure with the stated lease term, the sold-prices link, EPC
 * source area and rooms, auction results, the other areas a listing states,
 * your GDV, floor area in m² and ft² with its assumption, listing-area
 * conflicts and the valuation caveats. Saved reads only.
 */
import { Fragment } from 'react'
import { useAccess } from '../../api/queries'
import { useAreaPotential } from '../../api/areaPotential'
import type { CardItem, PropertyBundle } from '../../api/types'
import { effectivePropertyClassification, soldPricesUrl, type PropertyClassificationFacts } from '../../api/propertyClassification'
import { singleHomeValuationScope, valuationNoticeContent } from '../../api/valuationPresentation'
import { TILE_LABELS, type ExperienceSpec } from '../../experiences/registry'
import { Alert } from '../../ui/kit'
import { GdvValue } from './diligence/GdvValue'
import { EnergyDetails, EnergySummary, epcFloorArea } from './diligence/Energy'
import { SQFT_PER_SQM, money, num, plain } from './diligence/format'
import { Details, FloorAreaAssumptionNotice, FloorAreaValue, Note } from './diligence/ui'

type JsonRecord = Record<string, unknown>
const asRecord = (value: unknown): JsonRecord | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : null
const asRecords = (value: unknown): JsonRecord[] => Array.isArray(value) ? value.map(asRecord).filter((item): item is JsonRecord => item !== null) : []
const recordString = (record: JsonRecord | null, key: string) => { const value = record?.[key]; return typeof value === 'string' && value.trim() ? value : null }
const recordNumber = (record: JsonRecord | null, key: string) => { const value = record?.[key]; return typeof value === 'number' && Number.isFinite(value) ? value : null }

function useDiligenceCard(propertyId: string, spec: ExperienceSpec) {
  const access = useAccess()
  const readable = access.can('property.diligence', spec.modeKey)
  const area = useAreaPotential(propertyId, readable)
  return { access, readable, area: readable ? area.data ?? null : null }
}

/* ---------- overview: facts and description ---------- */

/** The Rightmove sold-prices link follows the saved subject, including a cleared dwelling type. */
export function useSoldPricesUrl(bundle: PropertyBundle, spec: ExperienceSpec, selected?: PropertyClassificationFacts | null): string | null {
  const { area } = useDiligenceCard(bundle.property.id, spec)
  const resolved = area?.card?.property ?? null
  const classification = effectivePropertyClassification(bundle.property, selected ?? resolved)
  return soldPricesUrl(resolved?.address?.postcode ?? bundle.property.postcode, classification)
}

export function OverviewSections({ bundle }: { bundle: PropertyBundle; spec: ExperienceSpec }) {
  const { property, listings } = bundle
  const listing = listings[0]
  const document = asRecord(property.document)
  const description = recordString(document, 'description')
  const statements = asRecords(asRecord(document?.descriptionAnalysis)?.unmapped)
    .map(item => ({ observation: recordString(item, 'observation'), quote: recordString(item, 'quote') }))
    .filter((item): item is { observation: string; quote: string } => Boolean(item.observation && item.quote
      && /\b(?:area|space|sqm|sqft)\b/i.test(item.observation)
      && /\b(?:sq\.?\s*(?:m|ft)|sqm|sqft|square\s+(?:metres?|meters?|feet|foot))\b|(?:m|ft)²/i.test(item.quote)
      && description?.toLowerCase().includes(item.quote.toLowerCase())))
    .slice(0, 4)
  return (
    <div className="sections">
      <div className="panel">
        <div className="panel-h"><span className="t-section">Original description</span><span className="t-faint">{listing?.sourceKey}</span></div>
        {description ? <p className="prose">{description}</p> : <p className="t-muted">No listing description is available.</p>}
        {statements.length > 0 ? (
          <div className="dil-statements">
            <b>Other areas stated in the listing</b>
            {statements.map((statement, index) => <p key={`${statement.quote}-${index}`}>{statement.observation}: “{statement.quote}”</p>)}
            <Note>These source statements may describe a whole building, plot or outbuilding. They do not supply a verified dwelling area for calculations.</Note>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* ---------- side panel: valuation & area ---------- */

export function ValuationAreaPanel({ bundle, item, spec }: { bundle: PropertyBundle; item: CardItem | undefined; spec: ExperienceSpec }) {
  const { property, media, epcAssessments } = bundle
  const { area } = useDiligenceCard(property.id, spec)
  const evidence = area?.card?.valuation ?? null
  const valuation = item?.card?.valuation
  const floorAreaSqm = evidence ? evidence.floorAreaSqm : valuation?.floorAreaSqm ?? null
  const assumed = evidence?.floorAreaAssumed ?? valuation?.floorAreaAssumed ?? false
  const gdvPence = evidence ? evidence.gdvPence : valuation?.gdvPence ?? null
  const lower = evidence ? evidence.gdvLowerPence : valuation?.gdvLowerPence ?? null
  const upper = evidence ? evidence.gdvUpperPence : valuation?.gdvUpperPence ?? null
  const document = asRecord(property.document)
  const description = recordString(document, 'description')
  const extracted = recordNumber(asRecord(document?.descriptionAnalysis), 'floorAreaSqm')
  const extractedText = extracted === null ? null : String(extracted).replace('.', '\\.')
  const listingAreaSqm = extracted !== null && extracted > 0 && extractedText && new RegExp(`(?:^|[^\\d])${extractedText}\\s*(?:m²|sq\\.?\\s*m|sqm)(?![a-z\\d])`, 'i').test(description ?? '') ? extracted : null
  const conflict = floorAreaSqm !== null && listingAreaSqm !== null && Math.abs(floorAreaSqm - listingAreaSqm) > Math.max(5, floorAreaSqm * 0.15)
  const epcAreaSqm = epcFloorArea(epcAssessments[0])
  const hasFloorplan = media.some(entry => entry.mediaKind === 'floorplan')
  const scores = (['resale', 'area', 'rent', 'family'] as const).map(key => {
    const d = item?.diligence
    const s = key === 'resale' ? d?.resalePotential : key === 'rent' ? d?.rentalPotential : key === 'area' ? d?.areaRisk : d?.familyFit
    return { key, label: TILE_LABELS[key], score: s?.score ?? null, partial: s?.state === 'partial' || (s?.assumedWeightPercent ?? 0) > 0 }
  })
  const scope = singleHomeValuationScope(area?.card ? area.card.property : item?.card?.property, area?.diligence?.inputQuality)
  const specialist = scope?.state === 'not_applicable'
  const notice = valuationNoticeContent(evidence ?? valuation, scope)
  const caveatRows = notice?.rows ?? []
  return (
    <div className="panel">
      <div className="panel-h"><span className="t-section">Valuation &amp; area</span></div>
      <dl className="kv">
        <dt>GDV ◆</dt><dd><GdvValue propertyId={property.id} snapshot={area?.personalSettings ?? bundle.personalSettings} systemGdvPence={gdvPence} compact /></dd>
        <dt>Range</dt><dd>{lower ? `${money(lower)} – ${money(upper)}` : '—'}</dd>
        <dt>Floor area</dt><dd><FloorAreaValue value={floorAreaSqm} assumed={assumed} assumption={evidence?.floorAreaAssumption} label={floorAreaSqm === null ? 'Unknown' : `${num(floorAreaSqm)} m² / ${plain(Math.round(floorAreaSqm * SQFT_PER_SQM))} ft²`} /></dd>
        {listingAreaSqm !== null && (floorAreaSqm === null || conflict) ? <><dt>Listing-stated area</dt><dd>{num(listingAreaSqm)} m²</dd></> : null}
        {scores.map(s => <Fragment key={s.key}><dt>{s.label}</dt><dd>{s.score === null ? '—' : Math.round(s.score)}{s.partial ? ' ▲' : ''}</dd></Fragment>)}
      </dl>
      <div className="dil-aside-notes">
        {!specialist ? <FloorAreaAssumptionNotice assumed={assumed} assumption={evidence?.floorAreaAssumption} /> : null}
        {!specialist && floorAreaSqm === null && hasFloorplan ? <Alert warn tight title="Floor area needs verification">A floor plan is available, but no verified internal area has been accepted for calculations. Check the scope of any printed total, including separately drawn outbuildings.</Alert> : null}
        {conflict ? <Alert warn tight title="Area sources disagree">The listing states {num(listingAreaSqm)} m², while the card uses {num(floorAreaSqm)} m²{epcAreaSqm === floorAreaSqm ? ' from the EPC' : ''}. {specialist ? 'Check which parts of the lot each measurement covers.' : 'Verify which measurement covers the property before relying on area-based calculations.'}</Alert> : null}
        <Note>Stored value and location evidence, not a deal verdict or maximum bid.</Note>
      </div>
      {caveatRows.length ? <div className="panel-h dil-caveats-h"><span className="t-section">Caveats · {caveatRows.length}</span></div> : null}
      {notice ? <p className="dil-note is-warn">{notice.title}</p> : null}
      {caveatRows.map((warning, index) => <div className="cav" key={index}><span className="g">▲</span><span>{warning}</span></div>)}
    </div>
  )
}

/* ---------- side panel: energy and decoded rooms ---------- */

function roomName(value: string | null, fallback: string): string {
  if (!value) return fallback
  const normalized = value.toLocaleLowerCase('en-GB')
  return normalized.charAt(0).toLocaleUpperCase('en-GB') + normalized.slice(1)
}

export function EnergyPanel({ bundle, spec }: { bundle: PropertyBundle; spec: ExperienceSpec }) {
  const access = useAccess()
  const epc = bundle.epcAssessments[0]
  const granted = spec.lot.diligence.includes('energy') && access.can('property.diligence', spec.modeKey) && access.can('diligence.energy', spec.modeKey)
  const analysis = asRecord(asRecord(bundle.property.document)?.floorplanAnalysis)
  const rooms = analysis?.schemaVersion === 3 && analysis?.promptVersion === 'floorplan-transcription-v2' ? asRecords(analysis.rooms) : []
  return (
    <>
      {granted ? (
        <div className="panel">
          <div className="panel-h"><span className="t-section">Energy</span><Details title="Energy performance"><EnergyDetails assessment={epc} /></Details></div>
          <EnergySummary assessment={epc} />
        </div>
      ) : null}
      {rooms.length > 0 ? (
        <div className="panel">
          <div className="panel-h"><span className="t-section">Rooms</span><span className="t-faint">Decoded from the floor plan</span></div>
          <div className="dil-rooms">{rooms.map((room, index) => {
            const length = recordNumber(room, 'lengthM')
            const width = recordNumber(room, 'widthM')
            const areaSqm = recordNumber(room, 'areaSqm')
            const dimensions = length !== null && width !== null ? `${plain(length)} × ${plain(width)} m` : recordString(room, 'dimensions')
            return (
              <div className="dil-room" key={`${recordString(room, 'name') ?? 'room'}-${index}`}>
                <span>{roomName(recordString(room, 'name'), `Room ${index + 1}`)}</span>
                {dimensions || areaSqm !== null ? <small>{[dimensions, areaSqm !== null ? `${plain(areaSqm)} m²` : null].filter(Boolean).join(' · ')}</small> : null}
              </div>
            )
          })}</div>
        </div>
      ) : null}
    </>
  )
}

